import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertOctagon,
  ArrowRight,
  BadgeCheck,
  Ban,
  Check,
  Eye,
  FlaskConical,
  History,
  Lock,
  PauseCircle,
  ShieldCheck,
  Star,
  Trash2,
  UserCog,
} from 'lucide-react';

import type { AdminStore } from '@/client/types/stores';
import { PERMISSIONS } from '@/client/config/permissions';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { useGate } from '@/client/hooks/useGate';
import { useStoreAuditQuery } from '@/client/api/stores/stores.query';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  useBanStore,
  useCloseStore,
  useDeleteStore,
  useFeatureStore,
  useOpenStore,
  usePublishStore,
  useReviewStore,
  useSetStoreTester,
  useSuspendStore,
  useTransferStore,
  useUnbanStore,
  useUnsuspendStore,
  useVerifyStore,
} from '@/client/api/stores/stores.mutation';

import { StoreOwnerLink, TimelineSkeleton, shortDate } from './shared';
import { IdLookup } from '../IdLookup';
import { ReasonDialog } from './ReasonDialog';
import { Toggle } from './settings/controls';
import { MODERATION_ACTIONS, auditActor, auditLabel, auditReason, auditWhen, relativeTime } from './auditLabels';

const S = PERMISSIONS.store;
const on = (v: unknown) => v === 1 || v === true;

type ActionKey =
  | 'suspend' | 'unsuspend' | 'verify' | 'unverify' | 'feature' | 'unfeature'
  | 'publish' | 'unpublish' | 'review_hold' | 'review_clear' | 'tester_on' | 'tester_off'
  | 'close' | 'open' | 'ban' | 'unban' | 'transfer' | 'delete';

interface DialogConfig {
  title: string;
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  /** High-impact: the admin must type the store's name to confirm. */
  typeToConfirm?: boolean;
}

const CONFIG: Record<ActionKey, DialogConfig> = {
  suspend: { title: 'Suspend store', description: 'Delists every listing, turns off instant purchase and freezes payouts. Orders already paid are honoured. The seller sees your reason.', confirmLabel: 'Suspend', destructive: true },
  unsuspend: { title: 'Lift suspension', description: 'Listings return if the store is otherwise open and its plan is active.', confirmLabel: 'Lift suspension' },
  verify: { title: 'Verify store', description: 'Shows the verified badge on the storefront and listings.', confirmLabel: 'Verify' },
  unverify: { title: 'Remove verification', description: 'Removes the verified badge.', confirmLabel: 'Remove', destructive: true },
  feature: { title: 'Feature store', description: 'Adds the store to featured placements.', confirmLabel: 'Feature' },
  unfeature: { title: 'Stop featuring', description: 'Removes the store from featured placements.', confirmLabel: 'Unfeature' },
  publish: { title: 'Publish store', description: 'Makes the storefront visible to buyers.', confirmLabel: 'Publish' },
  unpublish: { title: 'Unpublish store', description: 'Hides the storefront from buyers. Listings stay as they are.', confirmLabel: 'Unpublish', destructive: true },
  tester_on: { title: 'Mark as test store', description: 'Hides the storefront from live buyers; only testers can open it. Its listings are not changed.', confirmLabel: 'Mark as test', destructive: true },
  tester_off: { title: 'Mark as live store', description: 'Shows the storefront to everyone. Listings created by a test account stay hidden from live buyers.', confirmLabel: 'Mark as live' },
  review_hold: { title: 'Place review hold', description: 'Hides the store while its name is reviewed; the seller can’t re-open it meanwhile.', confirmLabel: 'Place hold', destructive: true },
  review_clear: { title: 'Clear review hold', description: 'Releases the name-review hold.', confirmLabel: 'Clear hold' },
  close: { title: 'Force-close store', description: 'Closes the store and delists every listing. Only staff can re-open it.', confirmLabel: 'Force-close', destructive: true, typeToConfirm: true },
  open: { title: 'Re-open store', description: 'Clears the force-close and re-opens the store. Refused if the plan has lapsed, or the store is suspended or on review hold.', confirmLabel: 'Re-open' },
  ban: { title: 'Ban store', description: 'Force-closes the store, delists every listing and bans it. The seller cannot re-open it.', confirmLabel: 'Ban store', destructive: true, typeToConfirm: true },
  unban: { title: 'Lift ban', description: 'Lifts the ban and the force-close, then tries to re-open the store.', confirmLabel: 'Lift ban' },
  transfer: { title: 'Transfer ownership', description: '', confirmLabel: 'Transfer', destructive: true, typeToConfirm: true },
  delete: { title: 'Delete store', description: 'Soft-deletes the store and delists everything. Records are kept for accounting, but the store is gone for the seller.', confirmLabel: 'Delete store', destructive: true, typeToConfirm: true },
};

// ── Standing ────────────────────────────────────────────────────────────────

type Standing = { label: string; summary: string; tone: string; icon: React.ReactNode };

const standingOf = (s: AdminStore): Standing => {
  if (on(s.deleted)) return { label: 'Deleted', summary: 'The store is gone for the seller. Records are kept.', tone: 'border-gray-300 bg-gray-50 text-gray-700', icon: <Trash2 className="h-6 w-6" /> };
  if (on(s.banned)) return { label: 'Banned', summary: 'Closed, delisted and banned. The seller cannot re-open it.', tone: 'border-red-300 bg-red-50 text-red-800', icon: <Ban className="h-6 w-6" /> };
  if (on(s.admin_closed)) return { label: 'Closed by staff', summary: 'Every listing is delisted. Only staff can re-open it.', tone: 'border-red-200 bg-red-50 text-red-800', icon: <Lock className="h-6 w-6" /> };
  if (on(s.suspended)) return { label: 'Suspended', summary: 'Listings delisted, instant purchase off, payouts frozen.', tone: 'border-orange-200 bg-orange-50 text-orange-800', icon: <PauseCircle className="h-6 w-6" /> };
  if (on(s.review)) return { label: 'On review hold', summary: 'Hidden from buyers while its name is reviewed.', tone: 'border-amber-200 bg-amber-50 text-amber-800', icon: <Eye className="h-6 w-6" /> };
  if (!on(s.active)) return { label: 'Closed by the seller', summary: 'The seller closed the store. No staff restriction.', tone: 'border-slate-200 bg-slate-50 text-slate-700', icon: <Lock className="h-6 w-6" /> };
  if (!on(s.published)) return { label: 'Unpublished', summary: 'Open, but the storefront isn’t visible to buyers.', tone: 'border-slate-200 bg-slate-50 text-slate-700', icon: <Eye className="h-6 w-6" /> };
  return { label: 'Good standing', summary: 'Open and visible to buyers. No restrictions.', tone: 'border-emerald-200 bg-emerald-50 text-emerald-800', icon: <ShieldCheck className="h-6 w-6" /> };
};

const Restriction: React.FC<{ label: string; reason?: unknown; date?: unknown }> = ({ label, reason, date }) => (
  <div className="rounded-lg bg-white/70 px-3 py-2 text-sm">
    <span className="font-semibold">{label}</span>
    {date ? <span className="ml-2 text-xs opacity-70">since {shortDate(String(date))}</span> : null}
    {reason ? <p className="mt-0.5 text-xs opacity-90">“{String(reason)}”</p> : null}
  </div>
);

// ── Enforcement ladder ──────────────────────────────────────────────────────

interface Step {
  key: string;
  label: string;
  effect: string;
  icon: React.ReactNode;
  active: boolean;
  apply: ActionKey;
  lift: ActionKey;
  code: string;
  applyTone: string;
  blockedBy?: string;
}

const ladder = (s: AdminStore): Step[] => [
  { key: 'review', label: 'Review hold', effect: 'Hidden while the name is reviewed', icon: <Eye className="h-4 w-4" />, active: on(s.review), apply: 'review_hold', lift: 'review_clear', code: S.review, applyTone: 'amber' },
  { key: 'suspend', label: 'Suspension', effect: 'Delisted, no new orders, payouts frozen', icon: <PauseCircle className="h-4 w-4" />, active: on(s.suspended), apply: 'suspend', lift: 'unsuspend', code: S.suspend, applyTone: 'orange' },
  { key: 'close', label: 'Force-close', effect: 'Closed; only staff can re-open', icon: <Lock className="h-4 w-4" />, active: on(s.admin_closed) && !on(s.banned), apply: 'close', lift: 'open', code: S.suspend, applyTone: 'red', blockedBy: on(s.banned) ? 'Part of the ban — lift the ban instead' : undefined },
  { key: 'ban', label: 'Ban', effect: 'Closed, delisted, permanently flagged', icon: <Ban className="h-4 w-4" />, active: on(s.banned), apply: 'ban', lift: 'unban', code: S.delete, applyTone: 'red' },
];

const ACTIVE_TONE: Record<string, string> = {
  amber: 'border-amber-300 bg-amber-50',
  orange: 'border-orange-300 bg-orange-50',
  red: 'border-red-300 bg-red-50',
};

const LadderStep: React.FC<{ step: Step; index: number; onAct: (k: ActionKey) => void }> = ({ step, index, onAct }) => (
  <div className={cn('flex flex-col rounded-xl border p-4 transition-colors', step.active ? ACTIVE_TONE[step.applyTone] : 'border-gray-200 bg-white')}>
    <div className="flex items-center justify-between">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Level {index + 1}</span>
      {step.active && (
        <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-800 shadow-sm">
          <span className="h-1.5 w-1.5 rounded-full bg-red-500" /> Active
        </span>
      )}
    </div>
    <div className="mt-2 flex items-center gap-2 text-sm font-semibold text-gray-900">
      {step.icon}
      {step.label}
    </div>
    <p className="mt-1 flex-1 text-xs text-gray-500">{step.effect}</p>
    <div className="mt-3">
      {step.blockedBy ? (
        <p className="text-xs text-gray-400">{step.blockedBy}</p>
      ) : step.active ? (
        <PermissionButton code={step.code} size="sm" variant="outline" className="w-full" onClick={() => onAct(step.lift)}>
          {CONFIG[step.lift].confirmLabel}
        </PermissionButton>
      ) : (
        <PermissionButton code={step.code} size="sm" variant="outline" className="w-full border-red-200 text-red-700 hover:bg-red-50" onClick={() => onAct(step.apply)}>
          {CONFIG[step.apply].confirmLabel}
        </PermissionButton>
      )}
    </div>
  </div>
);

// ── Trust & visibility switches ─────────────────────────────────────────────

const SwitchRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  hint: string;
  checked: boolean;
  code: string;
  meta?: string | null;
  onToggle: (next: boolean) => void;
}> = ({ icon, label, hint, checked, code, meta, onToggle }) => {
  const { allowed, reason } = useGate(code);
  return (
    <div className="flex items-center gap-3 py-3">
      <span className={cn('flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg', checked ? 'bg-teal-50 text-teal-600' : 'bg-gray-100 text-gray-400')}>
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">{meta || hint}</p>
      </div>
      <span title={allowed ? undefined : reason}>
        <Toggle checked={checked} disabled={!allowed} onChange={onToggle} />
      </span>
    </div>
  );
};

// ── Recent activity ─────────────────────────────────────────────────────────

const RecentActivity: React.FC<{ storeid: number; onOpenAudit?: () => void }> = ({ storeid, onOpenAudit }) => {
  const { data: entries = [], isLoading } = useStoreAuditQuery(storeid);
  const moderation = entries.filter((e) => e.action && MODERATION_ACTIONS.has(e.action)).slice(0, 8);

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <History className="h-4 w-4 text-gray-400" /> Moderation history
        </h3>
        {onOpenAudit && (
          <button type="button" onClick={onOpenAudit} className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:text-teal-900">
            Full audit <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>
      {isLoading ? (
        <TimelineSkeleton rows={4} />
      ) : moderation.length === 0 ? (
        <p className="text-sm text-gray-400">No moderation actions yet.</p>
      ) : (
        <ol className="relative space-y-4 border-l border-gray-200 pl-4">
          {moderation.map((e, i) => {
            const reason = auditReason(e);
            const when = auditWhen(e);
            return (
              <li key={i} className="relative">
                <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-teal-500 ring-1 ring-gray-200" />
                <p className="text-sm font-medium text-gray-900">{auditLabel(e)}</p>
                <p className="text-xs text-gray-500" title={when?.toLocaleString()}>
                  {auditActor(e)} · {relativeTime(when)}
                </p>
                {reason && <p className="mt-1 rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-600">“{reason}”</p>}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
};

// ── Page ────────────────────────────────────────────────────────────────────

/**
 * Where the store stands and every lever that changes it: standing banner with
 * the active restrictions, the enforcement ladder (review hold → suspension →
 * force-close → ban), trust & visibility switches, ownership and the danger
 * zone, next to the recent moderation history. Every action asks for a reason,
 * which is logged and — for restrictions — shown to the seller. Nothing changes
 * until the dialog is confirmed; force-close, ban, transfer and delete also
 * require typing the store's name.
 */
export const ModerationTab: React.FC<{ store: AdminStore; onOpenAudit?: () => void }> = ({ store, onOpenAudit }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState<ActionKey | null>(null);
  const [typed, setTyped] = useState('');
  const [owner, setOwner] = useState({ uid: store.uid != null ? String(store.uid) : '', oid: store.oid != null ? String(store.oid) : '' });

  const suspend = useSuspendStore();
  const unsuspend = useUnsuspendStore();
  const verify = useVerifyStore();
  const feature = useFeatureStore();
  const publish = usePublishStore();
  const review = useReviewStore();
  const tester = useSetStoreTester();
  const close = useCloseStore();
  const reopen = useOpenStore();
  const ban = useBanStore();
  const unban = useUnbanStore();
  const transfer = useTransferStore();
  const remove = useDeleteStore();
  const busy = [suspend, unsuspend, verify, feature, publish, review, tester, close, reopen, ban, unban, transfer, remove].some((m) => m.isPending);

  const toId = (v: string): number | null => (v.trim() === '' ? null : Number(v));
  const ownerChanged = toId(owner.uid) !== (store.uid ?? null) || toId(owner.oid) !== (store.oid ?? null);
  const ownerValid = toId(owner.uid) !== null || toId(owner.oid) !== null;

  const run = (key: ActionKey, reason: string) => {
    const id = store.storeid;
    const done = { onSuccess: () => setOpen(null) };
    switch (key) {
      case 'suspend': return suspend.mutate({ storeid: id, reason }, done);
      case 'unsuspend': return unsuspend.mutate({ storeid: id, reason }, done);
      case 'verify': return verify.mutate({ storeid: id, verified: true, reason }, done);
      case 'unverify': return verify.mutate({ storeid: id, verified: false, reason }, done);
      case 'feature': return feature.mutate({ storeid: id, featured: true, reason }, done);
      case 'unfeature': return feature.mutate({ storeid: id, featured: false, reason }, done);
      case 'publish': return publish.mutate({ storeid: id, published: true, reason }, done);
      case 'unpublish': return publish.mutate({ storeid: id, published: false, reason }, done);
      case 'tester_on': return tester.mutate({ storeid: id, tester: true, reason }, done);
      case 'tester_off': return tester.mutate({ storeid: id, tester: false, reason }, done);
      case 'review_hold': return review.mutate({ storeid: id, review: true, review_reason: reason }, done);
      case 'review_clear': return review.mutate({ storeid: id, review: false, reason }, done);
      case 'close': return close.mutate({ storeid: id, reason }, done);
      case 'open': return reopen.mutate({ storeid: id, reason }, done);
      case 'ban': return ban.mutate({ storeid: id, reason }, done);
      case 'unban': return unban.mutate({ storeid: id, reason }, done);
      case 'transfer': return transfer.mutate({ storeid: id, uid: toId(owner.uid), oid: toId(owner.oid), reason }, done);
      case 'delete': return remove.mutate({ storeid: id, reason }, { onSuccess: () => { setOpen(null); navigate('/admin/stores'); } });
    }
  };

  const ask = (key: ActionKey) => {
    setTyped('');
    setOpen(key);
  };
  const nameMatches = typed.trim().toLowerCase() === store.name.trim().toLowerCase();

  const standing = standingOf(store);
  const steps = ladder(store);
  const cfg = open ? CONFIG[open] : null;
  const description =
    open === 'transfer'
      ? `New owner: ${owner.uid ? `user #${owner.uid}` : 'no user'}${owner.oid ? `, org #${owner.oid}` : ''}. The current owner loses access to the store.`
      : cfg?.description;

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      <div className="space-y-6">
        {/* Standing */}
        <section className={cn('rounded-xl border p-5', standing.tone)}>
          <div className="flex items-start gap-4">
            <span className="mt-0.5 flex-shrink-0">{standing.icon}</span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide opacity-70">Standing</p>
              <h2 className="text-xl font-bold">{standing.label}</h2>
              <p className="mt-0.5 text-sm opacity-90">{standing.summary}</p>
            </div>
          </div>
          {(on(store.banned) || on(store.admin_closed) || on(store.suspended) || on(store.review)) && (
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {on(store.banned) && <Restriction label="Banned" reason={store.ban_reason} date={store.date_banned} />}
              {on(store.admin_closed) && !on(store.banned) && <Restriction label="Force-closed" reason={store.admin_close_reason} date={store.date_admin_closed} />}
              {on(store.suspended) && <Restriction label="Suspended" reason={store.suspend_reason} date={store.date_suspended} />}
              {on(store.review) && <Restriction label="Review hold" reason={store.review_reason} date={store.date_review_changed} />}
            </div>
          )}
        </section>

        {/* Enforcement ladder */}
        <section>
          <div className="mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Enforcement</h3>
            <p className="text-xs text-gray-500">Escalating restrictions. The seller sees the reason for each one and can’t undo it themselves.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {steps.map((step, i) => (
              <LadderStep key={step.key} step={step} index={i} onAct={ask} />
            ))}
          </div>
        </section>

        {/* Trust & visibility */}
        <section className="rounded-xl border border-gray-200 bg-white px-5 pb-2 pt-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <ShieldCheck className="h-4 w-4 text-gray-400" /> Trust & visibility
          </h3>
          <div className="divide-y divide-gray-100">
            <SwitchRow
              icon={<BadgeCheck className="h-5 w-5" />}
              label="Verified"
              hint="Trusted badge on the storefront and listings"
              meta={on(store.verified) && store.date_verified ? `Verified ${shortDate(String(store.date_verified))}` : null}
              checked={on(store.verified)}
              code={S.verify}
              onToggle={(v) => ask(v ? 'verify' : 'unverify')}
            />
            <SwitchRow
              icon={<Star className="h-5 w-5" />}
              label="Featured"
              hint="Shown in featured placements"
              meta={on(store.featured) && store.date_featured ? `Featured since ${shortDate(String(store.date_featured))}` : null}
              checked={on(store.featured)}
              code={S.feature}
              onToggle={(v) => ask(v ? 'feature' : 'unfeature')}
            />
            <SwitchRow
              icon={<Eye className="h-5 w-5" />}
              label="Published"
              hint="Storefront visible to buyers"
              checked={on(store.published)}
              code={S.publish}
              onToggle={(v) => ask(v ? 'publish' : 'unpublish')}
            />
            <SwitchRow
              icon={<FlaskConical className="h-5 w-5" />}
              label="Test store"
              hint="Storefront hidden from live buyers"
              checked={on(store.tester)}
              code={S.review}
              onToggle={(v) => ask(v ? 'tester_on' : 'tester_off')}
            />
          </div>
        </section>

        {/* Ownership */}
        <section className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="mb-3 flex items-center gap-2">
            <UserCog className="h-4 w-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-900">Ownership</h3>
          </div>
          <div className="mb-3 flex items-center gap-2 text-sm text-gray-600">
            <span>Owned by</span>
            <StoreOwnerLink store={store} />
          </div>
          <div className="flex flex-wrap items-start gap-2">
            <label className="space-y-1 text-sm">
              <span className="text-xs font-medium text-gray-500">New owner (user)</span>
              <IdLookup kind="user" value={owner.uid} onChange={(uid) => setOwner((o) => ({ ...o, uid }))} className="w-64" />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-xs font-medium text-gray-500">Organization</span>
              <IdLookup kind="org" value={owner.oid} onChange={(oid) => setOwner((o) => ({ ...o, oid }))} className="w-64" />
            </label>
            <PermissionButton code={S.transfer} variant="outline" disabled={!ownerChanged || !ownerValid} onClick={() => ask('transfer')}>
              Transfer
            </PermissionButton>
          </div>
        </section>

        {/* Danger zone */}
        <section className="rounded-xl border border-red-200 bg-red-50/40 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-red-800">
                <AlertOctagon className="h-4 w-4" /> Delete store
              </h3>
              <p className="mt-0.5 text-xs text-red-700/80">Soft delete. The store disappears for the seller; accounting records stay.</p>
            </div>
            {on(store.deleted) ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500"><Check className="h-3.5 w-3.5" /> Deleted</span>
            ) : (
              <PermissionButton code={S.delete} variant="destructive" onClick={() => ask('delete')}>
                <Trash2 className="h-4 w-4" /> Delete store
              </PermissionButton>
            )}
          </div>
        </section>
      </div>

      <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
        <RecentActivity storeid={store.storeid} onOpenAudit={onOpenAudit} />
      </aside>

      {open && cfg && (
        <ReasonDialog
          open
          title={cfg.title}
          description={description}
          confirmLabel={cfg.confirmLabel}
          destructive={cfg.destructive}
          reasonRequired
          loading={busy}
          confirmDisabled={cfg.typeToConfirm && !nameMatches}
          onConfirm={(reason) => run(open, reason)}
          onClose={() => setOpen(null)}
        >
          {cfg.typeToConfirm && (
            <label className="block space-y-1 rounded-lg border border-red-200 bg-red-50 p-3 text-sm">
              <span className="text-red-800">
                Type <b className="font-semibold">{store.name}</b> to confirm.
              </span>
              <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" className="bg-white" />
            </label>
          )}
        </ReasonDialog>
      )}
    </div>
  );
};
